<script lang="ts">
  import { onMount } from 'svelte';
  import { api } from '../js/api.js';
  import { toast } from '../js/toast.js';
  import { spinner } from '../js/spinner.js';
  import { push } from 'svelte-spa-router';

  export let invoiceUuid: string = '';

  /** Read invoice UUID from the address bar (single source of truth so it works regardless of router timing). */
  function getUuidFromHash(): string | undefined {
    if (typeof window === 'undefined') return undefined;
    const path = (window.location.hash || '').replace(/^#\/?/, '/').split('?')[0];
    if (!path.startsWith('/invoices/') || path === '/invoices') return undefined;
    const segment = path.slice('/invoices/'.length).split('/')[0]?.trim();
    return segment && segment.length > 0 ? segment : undefined;
  }

  function getInvoiceUuid(): string | undefined {
    return getUuidFromHash() || (invoiceUuid || '').trim() || undefined;
  }

  let invoice: any = null;
  let isLoading = false;

  onMount(async () => {
    // Always read from address bar first so we're correct even if Dashboard hasn't passed prop yet
    const uuid = getUuidFromHash() || (invoiceUuid || '').trim();
    if (!uuid) {
      push('/invoices');
      return;
    }
    await loadInvoice(uuid);
  });

  async function loadInvoice(uuid: string) {
    isLoading = true;
    spinner.show();
    try {
      const response = await api.getInvoice(uuid) as { success: boolean; data?: any; message?: string };
      if (response.success && response.data) {
        invoice = response.data;
      } else {
        toast.showNotification(false, response.message || 'Failed to load invoice');
        push('/invoices');
      }
    } catch (error) {
      toast.showNotification(false, 'Error loading invoice');
      console.error(error);
      push('/invoices');
    } finally {
      spinner.hide();
      isLoading = false;
    }
  }

  function formatPrice(price: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  }

  function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  let isDownloading = false;

  async function downloadInvoice() {
    if (isDownloading || !invoice) return;
    isDownloading = true;
    try {
      const uuid = invoice?.uuid || getInvoiceUuid() || invoiceUuid;
      if (uuid) await api.downloadInvoicePdf(uuid, invoice.invoice_number);
      toast.showNotification(true, 'Invoice downloaded');
    } catch (err) {
      toast.showNotification(false, 'Failed to download invoice');
    } finally {
      isDownloading = false;
    }
  }
</script>

<div class="invoice-detail">
  {#if isLoading}
    <div class="loading-state">Loading invoice...</div>
  {:else if invoice}
    <div class="invoice-header">
      <button class="back-button" on:click={() => push('/invoices')}>
        ← Back to Invoices
      </button>
      <div class="header-actions">
        <button
          type="button"
          class="download-button"
          disabled={isDownloading}
          on:click={downloadInvoice}
          title="Download invoice as PDF"
        >
          {#if isDownloading}
            <span class="download-spinner" aria-hidden="true"></span>
            Downloading…
          {:else}
            <i class="download icon" aria-hidden="true"></i>
            Download PDF
          {/if}
        </button>
      </div>
    </div>

    <div class="invoice-card">
      <div class="invoice-title-section">
        <h2 class="invoice-title">{(invoice.tax_amount ?? 0) > 0 ? 'Tax Invoice' : 'Invoice'} {invoice.invoice_number}</h2>
        <span 
          class="invoice-status" 
          style="background: {invoice.is_paid ? '#45c49620' : '#d91b4220'}; color: {invoice.is_paid ? '#45c496' : '#d91b42'}"
        >
          {invoice.is_paid ? 'Paid' : 'Unpaid'}
        </span>
      </div>

      <div class="invoice-info">
        <div class="info-row">
          <span class="info-label">Invoice Date:</span>
          <span class="info-value">{formatDate(invoice.invoice_date)}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Due Date:</span>
          <span class="info-value">{formatDate(invoice.due_date)}</span>
        </div>
        {#if invoice.paid_date}
          <div class="info-row">
            <span class="info-label">Paid Date:</span>
            <span class="info-value">{formatDate(invoice.paid_date)}</span>
          </div>
        {/if}
      </div>

      <div class="invoice-items">
        <h3 class="items-title">Items</h3>
        <table class="items-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Quantity</th>
              <th>Unit Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {#each invoice.items || [] as item}
              <tr>
                <td>{item.description}</td>
                <td>{item.quantity}</td>
                <td>{formatPrice(item.unit_price)}</td>
                <td>{formatPrice(item.total_price)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      <div class="invoice-totals">
        <div class="total-row">
          <span class="total-label">Subtotal:</span>
          <span class="total-value">{formatPrice(invoice.subtotal)}</span>
        </div>
        <div class="total-row">
          <span class="total-label">Tax ({invoice.tax_percentage}%):</span>
          <span class="total-value">{formatPrice(invoice.tax_amount)}</span>
        </div>
        <div class="total-row total-row-final">
          <span class="total-label">Total:</span>
          <span class="total-value">{formatPrice(invoice.total)}</span>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .invoice-detail {
    margin: 0 auto;
    padding: 2rem;
  }

  .invoice-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
  }

  .back-button {
    padding: 0.5rem 1rem;
    background: transparent;
    border: 1px solid var(--grey-secondary);
    color: var(--grey);
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    transition: background-color 0.2s;
  }

  .back-button:hover {
    background: var(--greyish);
  }

  .download-button {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 1rem;
    background: var(--primary);
    color: var(--white);
    border: 0;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    transition: background-color 0.2s;
  }

  .download-button:hover:not(:disabled) {
    background: var(--darkprimary);
  }

  .download-button:disabled {
    opacity: 0.8;
    cursor: not-allowed;
  }

  .download-button .icon {
    margin-right: 0.375rem;
  }

  .download-spinner {
    display: inline-block;
    width: 1rem;
    height: 1rem;
    margin-right: 0.375rem;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: var(--white);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    vertical-align: middle;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .invoice-card {
    background: var(--white);
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .invoice-title-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--greyish);
  }

  .invoice-title {
    font-size: 1.75rem;
    font-weight: 500;
    color: var(--grey);
    margin: 0;
  }

  .invoice-status {
    padding: 0.375rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .invoice-info {
    margin-bottom: 2rem;
  }

  .info-row {
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--greyish);
  }

  .info-label {
    color: var(--grey-secondary);
  }

  .info-value {
    font-weight: 500;
    color: var(--grey);
  }

  .invoice-items {
    margin-bottom: 2rem;
  }

  .items-title {
    font-size: 1.25rem;
    font-weight: 500;
    color: var(--grey);
    margin-bottom: 1rem;
  }

  .items-table {
    width: 100%;
    border-collapse: collapse;
  }

  .items-table th {
    text-align: left;
    padding: 0.75rem;
    background: var(--greyish);
    font-weight: 500;
    color: var(--grey-secondary);
    border-bottom: 1px solid var(--greyish);
  }

  .items-table td {
    padding: 0.75rem;
    border-bottom: 1px solid var(--greyish);
    color: var(--grey);
  }

  .invoice-totals {
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 2px solid var(--grey);
  }

  .total-row {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
  }

  .total-label {
    color: var(--grey-secondary);
  }

  .total-value {
    font-weight: 500;
    color: var(--grey);
  }

  .total-row-final {
    font-size: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid var(--greyish);
  }

  .total-row-final .total-label,
  .total-row-final .total-value {
    font-weight: 500;
    color: var(--grey);
  }

  .loading-state {
    text-align: center;
    padding: 4rem 2rem;
    color: var(--grey-secondary);
  }

  @media (max-width: 768px) {
    .invoice-detail {
      padding: 1rem;
    }

    .invoice-header {
      flex-direction: column;
      gap: 1rem;
      align-items: flex-start;
    }

    .items-table {
      font-size: 0.875rem;
    }
  }
</style>
